import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { apiConfig } from '../../lib/apiConfig';
import { SkeletonPublication } from '../../components/Skeleton/SkeletonCard';
import './PublicationList.css';

function getExcerpt(pub, maxLen = 140) {
  const raw = pub.excerpt || pub.description || pub.body || '';
  const text = raw.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  return text.length > maxLen ? text.slice(0, maxLen).trimEnd() + '...' : text;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

function readingTime(pub) {
  const raw = pub.body || pub.description || '';
  const text = raw.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  const words = text.split(' ').filter(Boolean).length;
  const mins = Math.max(1, Math.round(words / 200));
  return `${mins} min`;
}

export default function PublicationList() {
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState('Tous');

  useEffect(() => {
    async function fetchPublications() {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${apiConfig.publicationBase}/publications/blog/list`);
        const data = await res.json();
        const articles = data?.data || data?.items || [];
        if (data?.success && Array.isArray(articles)) {
          setPublications(articles);
        } else {
          setError('Erreur lors du chargement des articles');
        }
      } catch (e) {
        setError('Erreur lors du chargement des articles');
      } finally {
        setLoading(false);
      }
    }
    fetchPublications();
  }, []);

  const categories = ['Tous', ...Array.from(new Set(publications.map(p => p.category).filter(Boolean)))];
  const filtered = activeCategory === 'Tous' ? publications : publications.filter(p => p.category === activeCategory);

  return (
    <>
      <Helmet>
        <title>Blog — NEXUS SOLUTIONS</title>
        <meta name="description" content="Conseils, actualites digitales et etudes de cas pour les entrepreneurs et PME en Cote d'Ivoire. Par NEXUS SOLUTIONS." />
        <meta property="og:title" content="Blog — NEXUS SOLUTIONS" />
        <meta property="og:description" content="Conseils digitaux pour les entreprises ivoiriennes." />
      </Helmet>

      <div className="blog-page">
        <div className="blog-hero">
          <h1>Blog</h1>
          <p>Conseils, actualites digitales et etudes de cas pour les PME en Cote d'Ivoire.</p>
        </div>

        <div className="blog-container">
          {!loading && !error && categories.length > 1 && (
            <div className="blog-filters">
              {categories.map(cat => (
                <button
                  key={cat}
                  className={'blog-filter-btn' + (activeCategory === cat ? ' active' : '')}
                  onClick={() => setActiveCategory(cat)}
                  type="button"
                >
                  {cat}
                </button>
              ))}
            </div>
          )}

          {loading && (
            <div className="blog-grid">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="blog-card blog-card--skeleton">
                  <SkeletonPublication />
                </div>
              ))}
            </div>
          )}

          {error && <div className="blog-error">{error}</div>}

          {!loading && !error && filtered.length === 0 && (
            <div className="blog-empty">
              <div className="blog-empty__icon">✍️</div>
              <h2 className="blog-empty__title">On prepare des articles</h2>
              <p className="blog-empty__text">
                Nos premiers conseils digitaux pour les entreprises ivoiriennes arrivent bientot.
                Sois notifie des la publication !
              </p>
              <a
                href="mailto:contact@nxs-solutions.com"
                className="blog-empty__cta"
              >
                Me tenir informe par email
              </a>
            </div>
          )}

          {!loading && !error && filtered.length > 0 && (
            <div className="blog-grid">
              {filtered.map(pub => {
                const id = pub.id || pub._id;
                const excerpt = getExcerpt(pub);
                const rt = readingTime(pub);
                return (
                  <Link key={id} to={`/publications/${id}`} className="blog-card">
                    {pub.coverImage ? (
                      <div className="blog-card__img-wrap">
                        <img src={pub.coverImage} alt={pub.title} loading="lazy" className="blog-card__img" />
                      </div>
                    ) : (
                      <div className="blog-card__img-wrap blog-card__img-placeholder">
                        <span>NXS</span>
                      </div>
                    )}
                    <div className="blog-card__body">
                      <div className="blog-card__meta">
                        {pub.category && <span className="blog-card__cat">{pub.category}</span>}
                        <span className="blog-card__date">{formatDate(pub.publishedAt)}</span>
                        <span className="blog-card__rt">&#x23F1; {rt}</span>
                      </div>
                      <h2 className="blog-card__title">{pub.title}</h2>
                      {excerpt && <p className="blog-card__excerpt">{excerpt}</p>}
                      <span className="blog-card__cta">Lire l'article &rarr;</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
